import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = ({ onWaitlistClick, onDemoClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogoClick = (e) => {
    if (isHomePage) {
      e.preventDefault();
      scrollToTop();
    }
    // If not on home page, let the Link navigate normally
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0b]/80 backdrop-blur-xl border-b border-white/5">
      <div className="container mx-auto">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link 
              to="/" 
              onClick={handleLogoClick}
              className="text-xl font-bold heading-font text-white hover:text-gray-300 transition-colors"
            >
              Limitly
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {isHomePage ? (
              <>
                <button 
                  onClick={() => scrollToSection('features')}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Features
                </button>
                <button 
                  onClick={() => scrollToSection('how-it-works')}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  How it works
                </button>
                <button 
                  onClick={() => scrollToSection('pricing')}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Pricing
                </button>
                <button 
                  onClick={() => scrollToSection('faq')}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  FAQ
                </button>
                <Link 
                  to="/security"
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Security
                </Link>
              </>
            ) : (
              <>
                <Link 
                  to="/"
                  className={`text-sm transition-colors ${
                    location.pathname === '/' ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Home
                </Link>
                <Link 
                  to="/about"
                  className={`text-sm transition-colors ${
                    location.pathname === '/about' ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  About
                </Link>
                <Link 
                  to="/security"
                  className={`text-sm transition-colors ${
                    location.pathname === '/security' ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Security
                </Link>
                <Link 
                  to="/contact"
                  className={`text-sm transition-colors ${
                    location.pathname === '/contact' ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Contact
                </Link>
              </>
            )}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={onWaitlistClick}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Join waitlist
            </button>
            <button
              onClick={onDemoClick}
              className="px-5 py-2 bg-white/5 hover:bg-white/10 text-white text-sm font-medium rounded-lg transition-colors border border-white/10"
            >
              Request demo
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-gray-400 hover:text-white transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/5">
            <div className="flex flex-col space-y-4">
              {isHomePage ? (
                <>
                  <button 
                    onClick={() => scrollToSection('features')}
                    className="text-sm text-gray-400 hover:text-white transition-colors text-left"
                  >
                    Features
                  </button>
                  <button 
                    onClick={() => scrollToSection('how-it-works')}
                    className="text-sm text-gray-400 hover:text-white transition-colors text-left"
                  >
                    How it works
                  </button>
                  <button 
                    onClick={() => scrollToSection('pricing')}
                    className="text-sm text-gray-400 hover:text-white transition-colors text-left"
                  >
                    Pricing
                  </button>
                  <button 
                    onClick={() => scrollToSection('faq')}
                    className="text-sm text-gray-400 hover:text-white transition-colors text-left"
                  >
                    FAQ
                  </button>
                  <Link 
                    to="/security"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-sm text-gray-400 hover:text-white transition-colors text-left"
                  >
                    Security
                  </Link>
                </>
              ) : (
                <>
                  <Link 
                    to="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm transition-colors text-left ${
                      location.pathname === '/' ? 'text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Home
                  </Link>
                  <Link 
                    to="/about"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm transition-colors text-left ${
                      location.pathname === '/about' ? 'text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    About
                  </Link>
                  <Link 
                    to="/security"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm transition-colors text-left ${
                      location.pathname === '/security' ? 'text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Security
                  </Link>
                  <Link 
                    to="/contact"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm transition-colors text-left ${
                      location.pathname === '/contact' ? 'text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Contact
                  </Link>
                </>
              )}
              <div className="pt-2 space-y-3">
                <button
                  onClick={onWaitlistClick}
                  className="w-full px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors text-center"
                >
                  Join waitlist
                </button>
                <button
                  onClick={onDemoClick}
                  className="w-full px-5 py-2 bg-white/5 hover:bg-white/10 text-white text-sm font-medium rounded-lg transition-colors border border-white/10 text-center"
                >
                  Request demo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
